<template>
    <div class="bg-color rounded-5 p-5">
        <div class="w-100 rounded-4">
            <div
                class="d-flex justify-content-between"
                style="background: rgb(241, 247, 255); border-radius: 27px 27px 0 0"
            >
                <div class="flex align-items-center list-page-header">
                    <h1 class="px-5 py-3 page-heading">
                        {{ isNewMode ? 'Create Registration' : 'Registration Builder' }}
                    </h1>
                </div>
                <span class="px-5 d-flex align-items-center text-muted" v-if="applicationName">
                    Application: <strong class="ms-1">{{ applicationName }}</strong>
                </span>
            </div>

            <div class="w-100 bg-white" v-if="loading">
                <div class="d-grid justify-content-center align-items-center py-5" style="min-height: 400px">
                    <h4>Fetching information...</h4>
                </div>
            </div>

            <div v-else class="bg-white w-100 px-4 py-4 d-flex flex-column gap-4">
                <!-- Registration Name -->
                <div class="row">
                    <div class="col-md-6">
                        <label class="form-label fw-bold">Registration Name <span class="text-danger">*</span></label>
                        <input
                            type="text"
                            class="form-control"
                            v-model="registrationName"
                            placeholder="Enter registration name"
                            required
                        />
                    </div>
                </div>

                <!-- Registration Fields -->
                <div class="bg-white w-100 rounded-4 border">
                    <h1
                        class="px-5 py-3 page-heading d-flex justify-content-between"
                        style="background: #f1f7ff; font-weight: bold; border-radius: 14px 14px 0 0"
                    >
                        Registration Fields
                    </h1>
                    <div class="p-4">
                        <RegistrationFieldsTable
                            :registrationFields="registrationFields"
                            :fieldDefinitions="fieldDefinitions"
                            @update="handleRegistrationFieldUpdate"
                        />
                    </div>
                </div>
            </div>

            <!-- Action Buttons -->
            <div class="bg-white mx-0 py-5 d-flex justify-content-end px-5 gap-3">
                <button
                    class="other-btn cancel-btn"
                    @click="$router.push({ name: 'RegistrationBuilderList' })"
                >
                    Cancel
                </button>
                <button class="save-btn" :disabled="saving" @click="saveRegistration">
                    {{ saving ? 'Saving...' : 'Save' }}
                </button>
            </div>
        </div>
        <Loading v-if="loading || saving" />
    </div>
</template>

<script>
    import RegistrationFieldsTable from '@/components/application_builder/RegistrationFieldsTable.vue';
    import Loading from '@/components/general/loading/loading.vue';
    import { registrationConfig } from '@/util/apiRequests';

    // Fields that are always visible and non-configurable
    const ALWAYS_VISIBLE_KEYS = ['first_name', 'last_name'];

    // Default field definitions — fallback only if GET /config/registration/fields fails
    const DEFAULT_FIELD_DEFINITIONS = [
        { key: 'first_name', label: 'First Name', type: 'text', configurable: false },
        { key: 'middle_name', label: 'Middle Name', type: 'text', configurable: true },
        { key: 'last_name', label: 'Last Name', type: 'text', configurable: false },
        { key: 'suffix', label: 'Suffix', type: 'text', configurable: true },
        { key: 'email', label: 'Email', type: 'email', configurable: true },
        { key: 'phone', label: 'Phone', type: 'phone', configurable: true },
        { key: 'dob', label: 'Date of Birth', type: 'date', configurable: true },
        { key: 'medicaid_id', label: 'Medicaid ID', type: 'text', configurable: true },
        { key: 'medicare_id', label: 'Medicare ID', type: 'text', configurable: true },
        { key: 'guardian', label: 'Guardian', type: 'text', configurable: true },
    ];

    const DEFAULT_REGISTRATION_FIELDS = {
        middle_name: { visible: true, required: false },
        suffix: { visible: false, required: false },
        email: { visible: true, required: true },
        phone: { visible: true, required: true },
        dob: { visible: true, required: true },
        medicaid_id: { visible: true, required: false },
        medicare_id: { visible: true, required: false },
        guardian: { visible: false, required: false },
    };

    export default {
        name: 'RegistrationBuilder',
        components: {
            RegistrationFieldsTable,
            Loading,
        },
        data() {
            return {
                loading: true,
                saving: false,
                isNewMode: false,
                registrationId: null,
                registrationName: '',
                applicationName: '',
                originalItems: [],
                apiFieldsMap: {},
                fieldDefinitions: DEFAULT_FIELD_DEFINITIONS,
                registrationFields: JSON.parse(JSON.stringify(DEFAULT_REGISTRATION_FIELDS)),
            };
        },
        methods: {
            handleRegistrationFieldUpdate(fieldKey, property, value) {
                this.$set(this.registrationFields[fieldKey], property, value);
            },

            // ── Helpers ──

            parseArray(data) {
                if (Array.isArray(data)) return data;
                if (Array.isArray(data?.info)) return data.info;
                if (Array.isArray(data?.data)) return data.data;
                return [];
            },

            extractId(resData) {
                // API may return { recId: 5 } (number) or { recId: { insertId: 5 } } (MySQL raw)
                const recId = resData.recId;
                if (typeof recId === 'number') return recId;
                if (recId && typeof recId === 'object') return recId.insertId || recId.id;

                return resData.id
                    || resData.insertId
                    || resData.info?.id
                    || resData.info?.insertId
                    || (Array.isArray(resData.info) ? (resData.info[0]?.id || resData.info[0]?.insertId) : null)
                    || resData.data?.id
                    || resData.data?.insertId;
            },

            /** Build UI field definitions from fields catalog. Sets registration_field_id only. */
            buildFieldDefinitionsFromCatalog(fields) {
                const fieldsMap = {};
                const seenKeys = new Set();
                const defs = [];

                for (const field of fields) {
                    // field_key may be empty for some records (e.g. Last Name) — derive from name
                    const key = field.field_key || field.key
                        || field.name?.toLowerCase().replace(/\s+/g, '_');
                    if (!key || seenKeys.has(key)) continue;
                    seenKeys.add(key);

                    // In catalog, field.id IS the registration_field_id
                    fieldsMap[key] = {
                        registration_field_id: field.id,
                        item_id: null,
                    };
                    defs.push({
                        key,
                        label: field.field_label || field.label || field.name,
                        type: field.field_type || field.type || 'text',
                        configurable: !ALWAYS_VISIBLE_KEYS.includes(key),
                    });
                }

                this.apiFieldsMap = fieldsMap;
                return defs;
            },

            /** Build UI field definitions from saved items. Sets both registration_field_id and item_id. */
            buildFieldDefinitionsFromItems(items) {
                const seenKeys = new Set();
                const defs = [];

                const sorted = items.slice().sort((a, b) => (a.display_order || 0) - (b.display_order || 0));

                for (const item of sorted) {
                    const key = item.field_key || item.key;
                    if (!key || seenKeys.has(key)) continue;
                    seenKeys.add(key);

                    // In items, item.id is the item_id; registration_field_id is a separate field
                    // Merge into existing apiFieldsMap (which may already have registration_field_id from catalog)
                    if (this.apiFieldsMap[key]) {
                        this.apiFieldsMap[key].item_id = item.id;
                    } else {
                        this.apiFieldsMap[key] = {
                            registration_field_id: item.registration_field_id,
                            item_id: item.id,
                        };
                    }

                    defs.push({
                        key,
                        label: item.field_label || item.label || item.name,
                        type: item.field_type || item.type || 'text',
                        configurable: !ALWAYS_VISIBLE_KEYS.includes(key),
                    });
                }

                return defs;
            },

            /** Build visibility/required toggles from saved items (edit mode). */
            buildRegistrationFields(items) {
                const regFields = {};
                for (const item of items) {
                    const key = item.field_key || item.key;
                    if (!ALWAYS_VISIBLE_KEYS.includes(key)) {
                        regFields[key] = {
                            visible: true,
                            required: item.is_required === 1 || item.required === 1,
                        };
                    }
                }
                return regFields;
            },

            /** Build the list of fields the user wants to save. */
            buildVisibleFields() {
                const fields = [];
                let order = 1;

                console.log('[RegBuilder] buildVisibleFields — fieldDefinitions:', this.fieldDefinitions.map(d => d.key));
                console.log('[RegBuilder] buildVisibleFields — registrationFields:', JSON.stringify(this.registrationFields));

                for (const def of this.fieldDefinitions) {
                    const mapEntry = this.apiFieldsMap[def.key] || {};

                    if (!def.configurable) {
                        // Always-visible (first_name, last_name)
                        fields.push({
                            field_key: def.key,
                            field_label: def.label,
                            is_required: 1,
                            display_order: order++,
                            registration_field_id: mapEntry.registration_field_id || null,
                            item_id: mapEntry.item_id || null,
                        });
                    } else {
                        const uiField = this.registrationFields[def.key];
                        if (uiField && uiField.visible) {
                            fields.push({
                                field_key: def.key,
                                field_label: def.label,
                                is_required: uiField.required ? 1 : 0,
                                display_order: order++,
                                registration_field_id: mapEntry.registration_field_id || null,
                                item_id: mapEntry.item_id || null,
                            });
                        } else {
                            console.log('[RegBuilder] Skipping field (not visible):', def.key, 'uiField:', uiField);
                        }
                    }
                }

                console.log('[RegBuilder] buildVisibleFields result:', fields.map(f => f.field_key));
                return fields;
            },

            // ── Fetch ──

            /** Fetch the fields catalog (GET /config/registration/fields).
             *  Always called — provides registration_field_id needed by items API. */
            async fetchFieldsCatalog() {
                try {
                    const endpoint = registrationConfig.getFields();
                    console.log('[RegBuilder] Fetching fields catalog:', endpoint);
                    const response = await this.$api.get(endpoint);
                    console.log('[RegBuilder] Fields catalog response:', JSON.stringify(response.data));

                    const fields = this.parseArray(response.data);
                    if (!fields.length) return;

                    this.fieldDefinitions = this.buildFieldDefinitionsFromCatalog(fields);

                    // Default all configurable fields to visible + optional
                    const regFields = {};
                    for (const def of this.fieldDefinitions) {
                        if (def.configurable) {
                            regFields[def.key] = { visible: true, required: false };
                        }
                    }
                    this.registrationFields = regFields;
                } catch (error) {
                    console.warn('[RegBuilder] Fields catalog fetch failed, using defaults:', error);
                }
            },

            async fetchRegistrationConfig() {
                try {
                    const id = this.$route.query.id;
                    const application = this.$route.query.application;

                    // Always fetch the fields catalog first (provides registration_field_id)
                    await this.fetchFieldsCatalog();

                    if (!id) {
                        this.isNewMode = true;
                        this.loading = false;
                        return;
                    }

                    this.registrationId = id;
                    this.applicationName = application || '';

                    // Fetch config and items in parallel
                    const configEndpoint = registrationConfig.getById(id);
                    const itemsEndpoint = registrationConfig.getItems(id);
                    console.log('[RegBuilder] Fetching config:', configEndpoint);
                    console.log('[RegBuilder] Fetching items:', itemsEndpoint);

                    const [configResponse, itemsResponse] = await Promise.all([
                        this.$api.get(configEndpoint),
                        this.$api.get(itemsEndpoint),
                    ]);

                    console.log('[RegBuilder] Config response:', JSON.stringify(configResponse.data));
                    console.log('[RegBuilder] Items response:', JSON.stringify(itemsResponse.data));

                    // Process config — could be array or object
                    const rawConfig = configResponse.data;
                    const configData = Array.isArray(rawConfig)
                        ? rawConfig[0]
                        : Array.isArray(rawConfig?.info)
                          ? rawConfig.info[0]
                          : rawConfig?.info || rawConfig?.data || rawConfig;

                    if (!configData) {
                        this.$toasted.error('Registration config not found');
                        return;
                    }

                    this.registrationName = configData.name || '';
                    this.applicationName = configData.application || this.applicationName;

                    // Process items — try dedicated endpoint first, fall back to config.fields
                    let items = this.parseArray(itemsResponse.data);
                    console.log('[RegBuilder] Items endpoint count:', items.length);

                    if (!items.length && Array.isArray(configData.fields)) {
                        console.log('[RegBuilder] Using fields from config response instead');
                        items = configData.fields;
                    }

                    console.log('[RegBuilder] Final items count:', items.length, items);

                    if (items.length) {
                        this.originalItems = JSON.parse(JSON.stringify(items));

                        // Merge item_id from saved items into apiFieldsMap (catalog already has registration_field_id)
                        for (const item of items) {
                            const key = item.field_key || item.key;
                            if (this.apiFieldsMap[key]) {
                                this.apiFieldsMap[key].item_id = item.id;
                            } else {
                                this.apiFieldsMap[key] = {
                                    registration_field_id: item.registration_field_id,
                                    item_id: item.id,
                                };
                            }
                        }
                    }

                    // fieldDefinitions stays from catalog (ALL fields).
                    // Build registrationFields: saved items = visible, rest = hidden.
                    const savedKeys = new Set(items.map(i => i.field_key || i.key));
                    const regFields = {};
                    for (const def of this.fieldDefinitions) {
                        if (!def.configurable) continue; // first_name, last_name always visible
                        if (savedKeys.has(def.key)) {
                            const item = items.find(i => (i.field_key || i.key) === def.key);
                            regFields[def.key] = {
                                visible: true,
                                required: item ? (item.is_required === 1 || item.required === 1) : false,
                            };
                        } else {
                            regFields[def.key] = { visible: false, required: false };
                        }
                    }
                    this.registrationFields = regFields;
                } catch (error) {
                    console.error('[RegBuilder] Fetch failed:', error);
                    this.$toasted.error('Failed to load registration config');
                } finally {
                    this.loading = false;
                }
            },

            // ── Save ──

            validateForm() {
                if (!this.registrationName.trim()) {
                    this.$toasted.error('Registration Name is required');
                    return false;
                }
                return true;
            },

            async saveRegistration() {
                if (!this.validateForm()) return;

                try {
                    this.saving = true;

                    if (this.isNewMode) {
                        await this.saveNewRegistration();
                    } else {
                        await this.saveExistingRegistration();
                    }
                } catch (error) {
                    console.error('[RegBuilder] Save failed:', error);
                    this.$toasted.error('Failed to save registration config');
                } finally {
                    this.saving = false;
                }
            },

            async saveNewRegistration() {
                // Step 1: Create registration header
                const createEndpoint = registrationConfig.create();
                const createPayload = {
                    name: this.registrationName,
                    active: 1,
                };
                console.log('[RegBuilder] Step 1 — Creating registration:', createEndpoint, createPayload);

                const createResponse = await this.$api.put(createEndpoint, createPayload);
                console.log('[RegBuilder] Create response:', JSON.stringify(createResponse.data));

                const newId = this.extractId(createResponse.data);
                console.log('[RegBuilder] Extracted newId:', newId);

                if (!newId) {
                    console.error('[RegBuilder] No ID returned. Full response:', JSON.stringify(createResponse.data));
                    this.$toasted.error('Registration created but no ID returned');
                    this.$router.push({ name: 'RegistrationBuilderList' });
                    return;
                }

                // Step 2: Create each visible field as an item
                const visibleFields = this.buildVisibleFields();
                console.log('[RegBuilder] Step 2 — Creating', visibleFields.length, 'items');
                console.log('[RegBuilder] apiFieldsMap:', JSON.stringify(this.apiFieldsMap));

                for (const field of visibleFields) {
                    const itemEndpoint = registrationConfig.updateItem('new');
                    const itemPayload = {
                        registration_id: newId,
                        registration_field_id: field.registration_field_id,
                        field_key: field.field_key,
                        field_label: field.field_label,
                        field_type: field.field_type,
                        is_required: field.is_required,
                        display_order: field.display_order,
                    };
                    console.log('[RegBuilder] Creating item:', itemEndpoint, JSON.stringify(itemPayload));
                    const itemRes = await this.$api.put(itemEndpoint, itemPayload);
                    console.log('[RegBuilder] Item response:', JSON.stringify(itemRes.data));
                }

                this.$toasted.success('Registration created successfully');
                this.$router.push({ name: 'RegistrationBuilderList' });
            },

            async saveExistingRegistration() {
                // Step 1: Update registration header
                const updateEndpoint = registrationConfig.update(this.registrationId);
                const updatePayload = {
                    name: this.registrationName,
                    active: 1,
                };
                console.log('[RegBuilder] Step 1 — Updating registration:', updateEndpoint, updatePayload);
                const updateResponse = await this.$api.put(updateEndpoint, updatePayload);
                console.log('[RegBuilder] Update response:', JSON.stringify(updateResponse.data));

                // Step 2: Sync items (update existing, create new, delete removed)
                const visibleFields = this.buildVisibleFields();

                // Map original items by field_key for lookup
                const originalByKey = {};
                for (const item of this.originalItems) {
                    const key = item.field_key || item.key;
                    originalByKey[key] = item;
                }

                const visibleKeys = new Set(visibleFields.map(f => f.field_key));

                // Update or create items
                for (const field of visibleFields) {
                    const original = originalByKey[field.field_key];
                    const existingItemId = original ? (original.id || original.item_id) : null;

                    if (existingItemId) {
                        // Update existing item
                        const endpoint = registrationConfig.updateItem(existingItemId);
                        const payload = {
                            field_label: field.field_label,
                            is_required: field.is_required,
                            display_order: field.display_order,
                        };
                        console.log('[RegBuilder] Updating item:', endpoint, JSON.stringify(payload));
                        await this.$api.put(endpoint, payload);
                    } else {
                        // Create new item
                        const endpoint = registrationConfig.updateItem('new');
                        const payload = {
                            registration_id: this.registrationId,
                            registration_field_id: field.registration_field_id,
                            field_key: field.field_key,
                            field_label: field.field_label,
                            is_required: field.is_required,
                            display_order: field.display_order,
                        };
                        console.log('[RegBuilder] Creating new item:', endpoint, JSON.stringify(payload));
                        await this.$api.put(endpoint, payload);
                    }
                }

                // Delete items that were saved before but are now hidden
                console.log('[RegBuilder] visibleKeys:', [...visibleKeys]);
                console.log('[RegBuilder] originalItems keys:', this.originalItems.map(i => i.field_key || i.key));
                for (const item of this.originalItems) {
                    const key = item.field_key || item.key;
                    if (!visibleKeys.has(key)) {
                        console.log('[RegBuilder] Will delete item:', key, 'id:', item.id || item.item_id);
                        const itemId = item.id || item.item_id;
                        if (itemId) {
                            const endpoint = registrationConfig.deleteItem(itemId);
                            console.log('[RegBuilder] Deleting item:', endpoint);
                            await this.$api.delete(endpoint);
                        }
                    }
                }

                this.$toasted.success('Registration config saved successfully');
                this.$router.push({ name: 'RegistrationBuilderList' });
            },
        },
        created() {
            this.fetchRegistrationConfig();
        },
    };
</script>

<style scoped>
    .bg-color {
        background-color: rgba(255, 255, 255, 0.9);
    }

    .list-page-header {
        display: block;
        margin: 0;
        padding: 0;
        background: none;
    }

    .other-btn {
        background: #fff;
        box-shadow: 0px 8px 22px 6px rgba(0, 0, 0, 0.24), inset 0px 4px 4px rgba(220, 220, 220, 0.25);
        color: #5057c3;
        width: 200px;
        height: 50px;
        font-family: Roboto;
        font-size: 16px;
        border-radius: 5px;
        border: 1px solid 5057C3;
        font-weight: 500;
    }

    .cancel-btn {
        color: #fff;
        background: var(--button-color);
        width: 150px;
    }

    .save-btn {
        background: #5057c3;
        box-shadow: 0px 8px 22px 6px rgba(0, 0, 0, 0.24), inset 0px 4px 4px rgba(220, 220, 220, 0.25);
        color: white;
        width: 150px;
        height: 50px;
        font-family: Roboto;
        font-size: 16px;
        border-radius: 5px;
        border: none;
    }

    .save-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
</style>
